<div #searchWrapper class="search-wrapper">

  <!-- ── Trigger button ──────────────────────────────────────────────────── -->
  <button class="search-trigger" type="button" (click)="open()" aria-label="Search" aria-haspopup="dialog"
    [attr.aria-expanded]="isOpen()">
    <lucide-icon name="search" [size]="18" style="display: flex;" />
  </button>

  <!-- ── Modal ────────────────────────────────────────────────────────────── -->
  @if (isOpen()) {
  <div class="search-overlay" (click)="close()">
    <div class="search-modal" role="dialog" aria-modal="true" aria-label="Search" (click)="$event.stopPropagation()">

      <!-- Search row -->
      <div class="modal-search-row">
        <lucide-icon name="search" [size]="18" class="search-icon" style="display: flex;" />
        <input #searchInput class="search-input" type="text" placeholder="Search..." autocomplete="off"
          spellcheck="false" [value]="searchQuery()" (input)="onInput($event)" (keydown)="onKeyDown($event)"
          aria-label="Search" role="combobox" aria-haspopup="listbox" [attr.aria-expanded]="true" />
        <button class="icon-btn" type="button" aria-label="Filter">
          <lucide-icon name="filter" [size]="16" style="display: flex;" />
        </button>
        <button class="icon-btn" type="button" aria-label="Sort">
          <lucide-icon name="arrow-up-down" [size]="16" style="display: flex;" />
        </button>
      </div>

      <!-- Tabs -->
      <div class="tab-bar" role="tablist">
        @for (tab of tabDefs; track tab.id) {
        <button class="tab-pill" type="button" role="tab" [class.is-active]="activeTab() === tab.id"
          [attr.aria-selected]="activeTab() === tab.id" (click)="selectTab(tab.id)">
          {{ tab.label }}
        </button>
        }
      </div>

      <!-- Results — keyed on the active tab so the whole block remounts (and its fade-in
           keyframe retriggers) on every tab switch, without needing the Animations module -->
      <div class="tab-content">
        @for (tab of [activeTab()]; track tab) {

        @if (isLoading()) {
        <div class="search-state">
          <lucide-icon name="loader-2" [size]="20" class="spin-icon" style="display: flex;" />
          <span>Loading...</span>
        </div>

        } @else if (!hasResults()) {
        <div class="search-state">
          @if (searchQuery()) {
          <lucide-icon name="search-x" [size]="28" style="display: flex;" />
          <p class="state-title">No results for <strong>"{{ searchQuery() }}"</strong></p>
          <p class="state-sub">Try a different search term</p>
          } @else {
          <p class="state-title">Nothing here yet</p>
          }
        </div>

        } @else {
        <div class="search-results" role="listbox" aria-label="Search results">
          @for (item of filteredFlatItems(); track item.id; let i = $index) {

          @switch (item.type) {

          <!-- Section header (not interactive) -->
          @case ('section') {
          <div class="section-label">
            <span>{{ item.label }}</span>
            @if (item.count !== undefined) {
            <span class="section-count">{{ item.count }}</span>
            }
          </div>
          }

          <!-- Org-level navigation shortcut -->
          @case ('nav') {
          <button #searchItem class="search-item search-item--nav" [class.is-active]="activeIndex() === i"
            [class.is-disabled]="item.disabled" [disabled]="item.disabled" (click)="selectItem(item)"
            (mouseenter)="!item.disabled && activeIndex.set(i)" (mouseleave)="activeIndex.set(-1)" role="option"
            [attr.aria-selected]="activeIndex() === i" type="button">
            <span class="item-icon">
              <lucide-icon [name]="item.icon!" [size]="16" style="display: flex;" />
            </span>
            <span class="item-label">{{ item.label }}</span>
            @if (item.disabledTag) {
            <span class="disabled-tag">{{ item.disabledTag }}</span>
            } @else {
            <lucide-icon name="corner-down-left" [size]="13" class="item-enter" style="display: flex;" />
            }
          </button>
          }

          <!-- Project result -->
          @case ('project') {
          <button #searchItem class="search-item search-item--project" [class.is-active]="activeIndex() === i"
            (click)="selectItem(item)" (mouseenter)="activeIndex.set(i)" (mouseleave)="activeIndex.set(-1)" role="option"
            [attr.aria-selected]="activeIndex() === i" type="button">
            <span class="project-dot" [style.background]="item.color || '#5F42D9'"></span>
            <span class="project-details">
              <span class="project-name">{{ item.label }}</span>
              @if (item.description) {
              <span class="project-desc">{{ item.description }}</span>
              }
            </span>
            <lucide-icon name="corner-down-left" [size]="13" class="item-enter" style="display: flex;" />
          </button>
          }

          <!-- Project sub-action (Sites / Requests / Members) -->
          @case ('sub-action') {
          <button #searchItem class="search-item search-item--sub" [class.is-active]="activeIndex() === i"
            [style.--accent]="item.color || '#5F42D9'" (click)="selectItem(item)" (mouseenter)="activeIndex.set(i)"
            (mouseleave)="activeIndex.set(-1)" role="option" [attr.aria-selected]="activeIndex() === i" type="button">
            <span class="sub-rail"></span>
            <span class="sub-icon">
              <lucide-icon [name]="item.icon!" [size]="14" style="display: flex;" />
            </span>
            <span class="sub-label">{{ item.label }}</span>
            <lucide-icon name="corner-down-left" [size]="13" class="item-enter" style="display: flex;" />
          </button>
          }

          <!-- Recently visited item -->
          @case ('recent') {
          <button #searchItem class="search-item search-item--project" [class.is-active]="activeIndex() === i"
            (click)="selectItem(item)" (mouseenter)="activeIndex.set(i)" (mouseleave)="activeIndex.set(-1)" role="option"
            [attr.aria-selected]="activeIndex() === i" type="button">
            @if (item.icon) {
            <span class="item-icon">
              <lucide-icon [name]="item.icon" [size]="16" style="display: flex;" />
            </span>
            } @else {
            <span class="project-dot" [style.background]="item.color || '#5F42D9'"></span>
            }
            <span class="project-details">
              <span class="project-name">{{ item.label }}</span>
              @if (item.description) {
              <span class="project-desc">{{ item.description }}</span>
              }
            </span>
            <lucide-icon name="corner-down-left" [size]="13" class="item-enter" style="display: flex;" />
          </button>
          }

          <!-- Request assigned to the current user -->
          @case ('active-request') {
          <button #searchItem class="search-item search-item--request" [class.is-active]="activeIndex() === i"
            (click)="selectItem(item)" (mouseenter)="activeIndex.set(i)" (mouseleave)="activeIndex.set(-1)" role="option"
            [attr.aria-selected]="activeIndex() === i" type="button">
            <span class="request-details">
              <span class="request-name">{{ item.label }}</span>
              <span class="request-subtitle">— {{ item.subtitle }}</span>
            </span>
            @if (item.status) {
            <span class="status-badge" [class]="item.statusBadgeClass">{{ item.status }}</span>
            }
            <lucide-icon name="corner-down-left" [size]="13" class="item-enter" style="display: flex;" />
          </button>
          }

          <!-- Request pending the current user's approval -->
          @case ('pending-approval') {
          <button #searchItem class="search-item search-item--request" [class.is-active]="activeIndex() === i"
            (click)="selectItem(item)" (mouseenter)="activeIndex.set(i)" (mouseleave)="activeIndex.set(-1)" role="option"
            [attr.aria-selected]="activeIndex() === i" type="button">
            <span class="request-details">
              <span class="request-name">{{ item.label }}</span>
              <span class="request-subtitle">— {{ item.subtitle }}</span>
            </span>
            <lucide-icon name="corner-down-left" [size]="13" class="item-enter" style="display: flex;" />
          </button>
          }

          }
          }
        </div>
        }

        }
      </div>

      <!-- Keyboard hints -->
      <div class="search-footer">
        <span class="hint"><kbd>↑</kbd><kbd>↓</kbd> Navigate</span>
        <span class="hint"><kbd>↵</kbd> Open</span>
        <span class="hint"><kbd>Esc</kbd> Close</span>
      </div>

    </div>
  </div>
  }

</div>
